/**
 * Stub implementation of @sl/react-datafabric-components
 * Used when the local datafabric package is not available (e.g., in Docker builds)
 */
import React, { createContext, useContext, useMemo, useState } from 'react';

// UiToolkitProvider stub
export const UiToolkitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

interface ToastMessage {
  id: number;
  severity: ToastSeverity;
  summary?: string;
  detail?: string;
  life?: number;
}

interface ToastContextValue {
  showToast: (message: Partial<ToastMessage>) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warn: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
let toastIdCounter = 0;

const toastStyles: Record<ToastSeverity, { background: string; border: string; color: string }> = {
  success: { background: '#ecfdf3', border: '#ABEFC6', color: '#067647' },
  info: { background: '#eff8ff', border: '#B2DDFF', color: '#175CD3' },
  warn: { background: '#fffaeb', border: '#FEDF89', color: '#B54708' },
  error: { background: '#fef3f2', border: '#FECDCA', color: '#B42318' },
};

// ToastProvider stub
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = (id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  };

  const showToast = (message: Partial<ToastMessage>) => {
    const severity = (message?.severity || 'info') as ToastSeverity;
    const summary = message?.summary || '';
    const detail = message?.detail || '';
    const id = ++toastIdCounter;
    const life = message?.life ?? 5000;

    setToasts((current) => [...current, { id, severity, summary, detail, life }]);
    window.setTimeout(() => {
      removeToast(id);
    }, life);

    console[severity === 'error' ? 'error' : 'log'](`[Toast ${severity}] ${summary} ${detail}`.trim());
  };

  const value = useMemo<ToastContextValue>(
    () => ({
      showToast,
      success: (message: string) => showToast({ severity: 'success', summary: message }),
      error: (message: string) => showToast({ severity: 'error', summary: message }),
      info: (message: string) => showToast({ severity: 'info', summary: message }),
      warn: (message: string) => showToast({ severity: 'warn', summary: message }),
    }),
    [],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          width: 'min(24rem, calc(100vw - 2rem))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const style = toastStyles[toast.severity];
          return (
            <div
              key={toast.id}
              style={{
                pointerEvents: 'auto',
                background: style.background,
                border: `1px solid ${style.border}`,
                color: style.color,
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.15)',
                padding: '0.9rem 1rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ minWidth: 0 }}>
                  {toast.summary ? (
                    <div style={{ fontWeight: 700, marginBottom: toast.detail ? '0.2rem' : 0 }}>{toast.summary}</div>
                  ) : null}
                  {toast.detail ? <div style={{ color: '#344054', lineHeight: 1.4 }}>{toast.detail}</div> : null}
                </div>
                <button
                  type="button"
                  onClick={() => removeToast(toast.id)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: style.color,
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1,
                    padding: 0,
                  }}
                  aria-label="Dismiss toast"
                >
                  x
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

// ConfirmDialogProvider stub
export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// PageContainer stub
export const PageContainer: React.FC<{ children: React.ReactNode; title?: string; breadcrumbs?: any[] }> = ({ children, title }) => {
  return (
    <div>
      {children}
    </div>
  );
};

// Button stub
export const Button: React.FC<any> = ({
  children,
  label,
  icon,
  loading,
  onClick,
  disabled,
  className,
  severity,
  text,
  outlined,
  rounded,
  ...props
}) => {
  const classes = [
    'p-button',
    'p-component',
    text ? 'p-button-text' : '',
    outlined ? 'p-button-outlined' : '',
    rounded ? 'p-button-rounded' : '',
    severity ? `p-button-${severity}` : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const buttonContent = children ?? label;

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={classes}
      {...props}
      style={{ padding: '8px 16px', cursor: disabled ? 'not-allowed' : 'pointer', ...props.style }}
    >
      {loading ? <span className="p-button-icon p-button-loading-icon pi pi-spinner pi-spin" /> : null}
      {!loading && icon ? <span className={`p-button-icon ${icon}`} /> : null}
      {buttonContent ? <span className="p-button-label">{buttonContent}</span> : null}
    </button>
  );
};

// InputTextarea stub
export const InputTextarea: React.FC<any> = ({ value, onChange, placeholder, rows = 4, ...props }) => {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      {...props}
      style={{ width: '100%', padding: '8px', fontFamily: 'inherit', ...props.style }}
    />
  );
};

// Typography stub
export const Typography: React.FC<{ variant?: string; children: React.ReactNode; [key: string]: any }> = ({ variant, children, ...props }) => {
  const Tag = variant === 'h1' ? 'h1' : variant === 'h2' ? 'h2' : variant === 'h3' ? 'h3' : 'p';
  return <Tag {...props}>{children}</Tag>;
};

// ProgressSpinner stub
export const ProgressSpinner: React.FC<any> = () => {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
      <div style={{ border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// DataGrid stub (using basic table)
export const DataGrid: React.FC<any> = (props) => {
  const { data = [], columns = [], onStateChange, state, ...rest } = props;

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            {columns.map((col: any, i: number) => (
              <th key={i} style={{ border: '1px solid #ddd', padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>
                {col.header || col.field}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No data available
              </td>
            </tr>
          ) : (
            data.map((row: any, i: number) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                {columns.map((col: any, j: number) => (
                  <td key={j} style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {col.body ? col.body(row) : row[col.field]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// DataGridColumn interface/type
export interface DataGridColumn {
  field: string;
  header: string;
  body?: (row: any) => React.ReactNode;
  sortable?: boolean;
  filter?: boolean;
  width?: string;
}

// DataGridState interface/type
export interface DataGridState {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>;
}

// useToast stub
export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    const showToast = (message: any) => {
      const level = message?.severity || 'info';
      const summary = message?.summary || '';
      const detail = message?.detail || '';
      console[level === 'error' ? 'error' : 'log'](`[Toast ${level}] ${summary} ${detail}`.trim());
    };

    return {
      showToast,
      success: (message: string) => console.log('[Toast Success]', message),
      error: (message: string) => console.error('[Toast Error]', message),
      info: (message: string) => console.info('[Toast Info]', message),
      warn: (message: string) => console.warn('[Toast Warning]', message),
    };
  }

  return context;
};

// useConfirmDialog stub
export const useConfirmDialog = () => {
  return {
    confirm: (options: any) => {
      const result = window.confirm(options.message || 'Are you sure?');
      if (result && options.accept) {
        options.accept();
      } else if (!result && options.reject) {
        options.reject();
      }
    },
  };
};

// Export everything that might be used
// Note: DataGridColumn and DataGridState are TypeScript types/interfaces and cannot be exported as runtime values
export default {
  UiToolkitProvider,
  ToastProvider,
  ConfirmDialogProvider,
  PageContainer,
  Button,
  InputTextarea,
  Typography,
  ProgressSpinner,
  DataGrid,
  useToast,
  useConfirmDialog,
};
